import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function SelbsthilfePage() {
  return (
    <div className="bg-white" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#FAF9F7] to-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
                Selbsthilfe & Gemeinschaft
              </span>
              <h1 className="text-4xl md:text-[48px] font-bold text-gray-900 leading-tight tracking-tight mb-6">
                Niemand muss seinen Weg allein gehen.
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed mb-6">
                Selbsthilfe schafft Begegnung, Verständnis und gegenseitige Unterstützung.
              </p>
              <p className="text-[17px] text-gray-600 leading-relaxed mb-8">
                asK-mi möchte Selbsthilfegruppen und selbstorganisierte Initiativen unterstützen, 
                die Menschen mit besonderen Herausforderungen und ihre Familien begleiten.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/unterstuetzung-anfragen"
                  className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-7 py-3.5 rounded-md font-medium transition-colors"
                >
                  Unterstützung anfragen
                </Link>
                <Link 
                  to="/foerderer-werden"
                  className="inline-flex items-center gap-2 border border-[#E8E4DE] hover:border-[#C4A962] text-gray-900 hover:text-[#A8923F] px-7 py-3.5 rounded-md font-medium transition-colors"
                >
                  Förderer werden
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/fcf2b3d82011364cb855d9b991e99d278b7a0d58a24f6dfbea32827abb2699a5.jpeg" 
                alt="Gemeinschaft und authentische Begegnung"
                className="w-full h-[400px] md:h-[480px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Warum Selbsthilfe */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Warum Selbsthilfe
            </span>
            <h2 className="text-3xl md:text-[38px] font-bold text-gray-900 tracking-tight mb-6">
              Weil der Mensch den Menschen braucht.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Gerade im Umfeld von Neurodiversität erleben viele Menschen, dass sie mit ihren 
              Erfahrungen, Fragen und Herausforderungen allein bleiben. Selbsthilfe kann einen 
              Raum schaffen, in dem Menschen verstanden werden, Erfahrungen teilen, voneinander 
              lernen und gemeinsam neue Wege entwickeln.
            </p>
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="rounded-xl overflow-hidden h-[240px]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/a67cc6d1078d4458d3504a233075192b0418b5bc2efbc3e85acf73872521718e.jpeg" 
                alt="Zweiergespräch und Verständnis"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-xl overflow-hidden h-[240px]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/3052d869b03defd54e88eceb682182670c0a74a913ed846c04d644a63bbd40c0.jpeg" 
                alt="Gemeinsames Arbeiten"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="rounded-xl overflow-hidden h-[240px]">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/d15332ffe234a8dde0c9445d896152f835808b8c432a1283e3fe4c7dd6198c7e.jpeg" 
                alt="Unterstützung und Begleitung"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Wen wir unterstützen */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Wen wir im Bereich Selbsthilfe unterstützen
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight">
              Gemeinsam stärker.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 border border-[#E8E4DE] hover:border-[#C4A962] hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-5">👥</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Selbsthilfegruppen</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Für Menschen mit unterschiedlichen Formen von Neurodiversität:
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A962] mt-1">•</span>
                  <span>ADS / ADHS</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A962] mt-1">•</span>
                  <span>Autismus-Spektrum</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A962] mt-1">•</span>
                  <span>Asperger-Spektrum</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C4A962] mt-1">•</span>
                  <span>Weitere neurodiverse Ausprägungen</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-[#E8E4DE] hover:border-[#C4A962] hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-5">👨‍👩‍👧</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Angehörige & Familien</h3>
              <p className="text-gray-600 leading-relaxed">
                Selbsthilfeangebote für Eltern, Familien und Angehörige von Kindern, 
                Jugendlichen und jungen Erwachsenen, die in ihrer besonderen Situation 
                Austausch und Unterstützung suchen.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-[#E8E4DE] hover:border-[#C4A962] hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-5">🧑‍🤝‍🧑</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Junge Menschen</h3>
              <p className="text-gray-600 leading-relaxed">
                Selbstorganisierte Gruppen und Angebote, in denen Jugendliche und junge 
                Erwachsene Erfahrungen austauschen und sich gegenseitig unterstützen können.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-[#E8E4DE] hover:border-[#C4A962] hover:shadow-lg transition-all duration-300">
              <div className="text-3xl mb-5">🤝</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Begleitende Initiativen</h3>
              <p className="text-gray-600 leading-relaxed">
                Initiativen und Projekte, die Begegnung, Austausch, Teilhabe und 
                gegenseitige Unterstützung ermöglichen und neue Wege schaffen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Was asK-mi fördern kann */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Formen der Unterstützung
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight mb-6">
              Was asK-mi fördern kann.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              asK-mi unterstützt nicht ausschließlich durch finanzielle Mittel. 
              Welche Unterstützung möglich ist, wird individuell geprüft.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { 
                icon: '💰', 
                title: 'Finanzielle Förderung', 
                text: 'Unterstützung bei der Umsetzung konkreter Vorhaben und Projekte.' 
              },
              { 
                icon: '🏠', 
                title: 'Räume & Begegnung', 
                text: 'Unterstützung bei der Schaffung geeigneter Räume für Austausch und Begegnung.' 
              },
              { 
                icon: '🎁', 
                title: 'Material & Ausstattung', 
                text: 'Unterstützung bei notwendigen Materialien, Ausstattung oder digitalen Möglichkeiten.' 
              },
              { 
                icon: '🔗', 
                title: 'Vernetzung', 
                text: 'Zusammenbringen von Selbsthilfegruppen mit Einrichtungen, Fachleuten und Partnern.' 
              },
              { 
                icon: '📈', 
                title: 'Entwicklung', 
                text: 'Unterstützung bei der Weiterentwicklung bestehender und Entwicklung neuer Formate.' 
              },
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-[#F5F3F0] rounded-xl p-8 hover:bg-white hover:shadow-md hover:border-[#C4A962] border border-transparent transition-all duration-300"
              >
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="font-semibold text-gray-900 text-lg mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
            <div className="bg-[#F5F3F0] rounded-xl p-8 flex flex-col justify-center border border-dashed border-[#E8E4DE]">
              <p className="text-gray-500 text-sm mb-4">
                Sie haben ein Vorhaben im Bereich Selbsthilfe?
              </p>
              <Link 
                to="/unterstuetzung-anfragen"
                className="text-[#A8923F] font-medium hover:underline flex items-center gap-1"
              >
                Unterstützung anfragen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fördermodell */}
      <section className="py-24 md:py-32 bg-[#FAF9F7]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
              Wie Förderung funktioniert
            </span>
            <h2 className="text-3xl md:text-[42px] font-bold text-gray-900 tracking-tight">
              Förderung, die ankommt.
            </h2>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-[#E8E4DE]">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-3 text-2xl">
                  🤝
                </div>
                <p className="font-semibold text-gray-900 text-sm">Förderer</p>
              </div>
              <div className="text-xl text-[#C4A962]">→</div>
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#C4A962] flex items-center justify-center mx-auto mb-3">
                  <img src="/images/brand/ask-mi-logo-clean.webp" alt="asK-mi" className="h-12 w-auto" />
                </div>
                <p className="font-semibold text-gray-900 text-sm">asK-mi</p>
                <p className="text-xs text-gray-500">Prüfung & Auswahl</p>
              </div>
              <div className="text-xl text-[#C4A962]">→</div>
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-3 text-2xl">
                  ✓
                </div>
                <p className="font-semibold text-gray-900 text-sm">Unterstützung</p>
                <p className="text-xs text-gray-500">Gezielt & transparent</p>
              </div>
              <div className="text-xl text-[#C4A962]">→</div>
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-3 text-2xl">
                  👥
                </div>
                <p className="font-semibold text-gray-900 text-sm">Gruppen & Initiativen</p>
              </div>
              <div className="text-xl text-[#C4A962]">→</div>
              <div className="text-center">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#F5F3F0] border-2 border-[#E8E4DE] flex items-center justify-center mx-auto mb-3 text-2xl">
                  💛
                </div>
                <p className="font-semibold text-gray-900 text-sm">Menschen & Familien</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Förderung beginnt */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/d4ed44b3-63d8-417d-957c-4836bdf688a4/images/fcf2b3d82011364cb855d9b991e99d278b7a0d58a24f6dfbea32827abb2699a5.jpeg" 
                alt="Selbsthilfe - Menschen füreinander da"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <span className="text-xs font-semibold text-[#C4A962] uppercase tracking-[2px] mb-4 block">
                Teil unserer Mission
              </span>
              <h2 className="text-3xl md:text-[38px] font-bold text-gray-900 tracking-tight mb-6">
                Förderung beginnt dort, wo Menschen füreinander da sind.
              </h2>
              <div className="text-[17px] text-gray-600 leading-relaxed space-y-4">
                <p>
                  asK-mi versteht Förderung nicht nur als finanzielle Unterstützung.
                </p>
                <p>
                  Förderung bedeutet auch, <strong className="text-gray-900">Menschen miteinander zu verbinden</strong>, 
                  Räume zu schaffen, Wissen zugänglich zu machen und Strukturen zu stärken, 
                  in denen Menschen sich gegenseitig unterstützen können.
                </p>
                <p>
                  Deshalb gehört die Unterstützung von Selbsthilfegruppen und selbstorganisierten 
                  Initiativen zu unserem Förderverständnis.
                </p>
              </div>
              <div className="mt-8">
                <Link 
                  to="/was-wir-tun"
                  className="text-[#A8923F] font-medium hover:underline flex items-center gap-2"
                >
                  Mehr über unsere Arbeit <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-gray-900 text-white text-center">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="text-3xl md:text-[42px] font-bold tracking-tight mb-6">
            Sie engagieren sich in der Selbsthilfe?
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            Sie engagieren sich in einer Selbsthilfegruppe, Initiative oder einem Projekt 
            und möchten Menschen aus dem neurodiversen Spektrum und ihre Familien unterstützen? 
            Stellen Sie uns Ihr Vorhaben vor.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/unterstuetzung-anfragen"
              className="inline-flex items-center justify-center gap-2 bg-[#C4A962] hover:bg-[#D4B972] text-gray-900 px-9 py-4 rounded-md font-semibold text-lg transition-all hover:-translate-y-0.5"
            >
              Unterstützung anfragen
            </Link>
            <Link 
              to="/foerderer-werden"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-white/50 text-white px-9 py-4 rounded-md font-semibold text-lg transition-all hover:-translate-y-0.5"
            >
              Förderer werden
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
